import { useEffect, useRef, useState } from "react";
import api, { fmt, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  ClipboardList, Plus, Trash2, FileText, FileSpreadsheet, Sparkles, Loader2,
  AlertTriangle, Lightbulb, ArrowLeft, TrendingUp, Paperclip, Upload, Download, BarChart3,
} from "lucide-react";

const SEV = {
  alta: "bg-destructive/15 text-destructive border-destructive/40",
  media: "bg-amber-500/15 text-amber-600 border-amber-500/40",
  baja: "bg-muted text-muted-foreground border-border",
};

const Field = ({ label, children }) => (
  <label className="flex flex-col gap-1">
    <span className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</span>
    {children}
  </label>
);

export default function EstimacionesView() {
  const { user, access } = useAuth();
  const canWrite = user?.role !== "consulta" && (access?.active ?? true);

  const [presupuestos, setPresupuestos] = useState([]);
  const [pid, setPid] = useState("");
  const [list, setList] = useState([]);
  const [presuInfo, setPresuInfo] = useState(null);
  const [current, setCurrent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [keySet, setKeySet] = useState(true);
  const [anexos, setAnexos] = useState([]);
  const [porPartida, setPorPartida] = useState(false);
  const [resumen, setResumen] = useState(null);
  const [resumenHasta, setResumenHasta] = useState("");
  const [resumenLoading, setResumenLoading] = useState(false);
  const anexoInputRef = useRef(null);
  const importInputRef = useRef(null);

  useEffect(() => {
    if (!user) return;
    (async () => {
      const { data } = await api.get("/presupuestos");
      setPresupuestos(data);
      if (data.length) setPid((prev) => prev || data[0].id);
      try { const s = await api.get("/settings"); setKeySet(s.data.gemini_key_set); } catch { /* noop */ }
    })();
  }, [user]);

  const loadList = async (id) => {
    if (!id) return;
    const { data } = await api.get(`/presupuestos/${id}/estimaciones`);
    setList(data.estimaciones);
    setPresuInfo(data.presupuesto);
  };
  useEffect(() => { loadList(pid); setCurrent(null); setAnexos([]); setResumen(null); }, [pid]);

  const loadAnexos = async (eid) => {
    try { const { data } = await api.get(`/estimaciones/${eid}/anexos`); setAnexos(data); }
    catch { setAnexos([]); }
  };

  const open = async (eid) => {
    setLoading(true);
    setResumen(null);
    try {
      const { data } = await api.get(`/estimaciones/${eid}`);
      setCurrent(data);
      loadAnexos(eid);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const crear = async () => {
    try {
      const { data } = await api.post(`/presupuestos/${pid}/estimaciones`, {
        periodo_inicio: "", periodo_fin: "", anticipo_pct: 0, amortizacion_pct: 0,
        retencion_pct: 5, iva_pct: 16, conceptos: [],
      });
      toast.success(`Estimación #${data.numero} creada`);
      await loadList(pid);
      setCurrent(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const remove = async (eid) => {
    if (!window.confirm("¿Eliminar esta estimación?")) return;
    try {
      await api.delete(`/estimaciones/${eid}`);
      if (current?.id === eid) setCurrent(null);
      await loadList(pid);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const setHeader = (k, v) => setCurrent((c) => ({ ...c, [k]: v }));
  const setCant = (nodeId, v) =>
    setCurrent((c) => ({
      ...c,
      conceptos: c.conceptos.map((r) => (r.node_id === nodeId ? { ...r, cant_periodo: v } : r)),
    }));

  const save = async () => {
    try {
      const body = {
        periodo_inicio: current.periodo_inicio || "",
        periodo_fin: current.periodo_fin || "",
        anticipo_pct: Number(current.anticipo_pct) || 0,
        amortizacion_pct: Number(current.amortizacion_pct) || 0,
        retencion_pct: Number(current.retencion_pct) || 0,
        iva_pct: Number(current.iva_pct) || 0,
        conceptos: current.conceptos.map((r) => ({ node_id: r.node_id, cantidad: Number(r.cant_periodo) || 0 })),
      };
      const { data } = await api.put(`/estimaciones/${current.id}`, body);
      setCurrent((c) => ({ ...data, ia: c.ia, presupuesto: c.presupuesto }));
      await loadList(pid);
      toast.success("Estimación guardada");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const analizarIA = async () => {
    setAiLoading(true);
    try {
      const { data } = await api.post(`/estimaciones/${current.id}/ia`);
      setCurrent((c) => ({ ...c, ia: data.ia }));
      toast.success("Análisis de la IA generado");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setAiLoading(false); }
  };

  const download = async (kind) => {
    try {
      const res = await api.get(`/estimaciones/${current.id}/export/${kind}`, { params: porPartida ? { por_partida: true } : {}, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `estimacion_${current.numero}.${kind === "excel" ? "xlsx" : "pdf"}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "No se pudo exportar"); }
  };

  const exportJson = async () => {
    try {
      const res = await api.get(`/estimaciones/${current.id}/export/json`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `estimacion_${current.numero}.emauran.json`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("No se pudo exportar"); }
  };

  const importJson = async (ev) => {
    const f = ev.target.files?.[0]; ev.target.value = ""; if (!f) return;
    try {
      const bundle = JSON.parse(await f.text());
      const { data } = await api.post(`/estimaciones/import`, bundle);
      toast.success(`Estimación importada: ${data.nombre}`);
      const { data: presus } = await api.get("/presupuestos");
      setPresupuestos(presus);
      setPid(data.presupuesto_id);
      setTimeout(() => open(data.estimacion_id), 350);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Archivo inválido"); }
  };

  const onAnexoFiles = async (ev) => {
    const files = Array.from(ev.target.files || []); ev.target.value = "";
    for (const f of files) {
      if (f.size > 9 * 1024 * 1024) { toast.error(`"${f.name}" supera 9 MB`); continue; }
      try {
        const dataUrl = await new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(f); });
        const b64 = String(dataUrl).split(",")[1] || "";
        await api.post(`/estimaciones/${current.id}/anexos`, { nombre: f.name, mime: f.type || "application/octet-stream", data: b64, size: f.size });
      } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || `No se pudo subir ${f.name}`); }
    }
    toast.success("Anexos actualizados");
    loadAnexos(current.id);
  };

  const removeAnexo = async (aid) => {
    if (!window.confirm("¿Eliminar este anexo?")) return;
    try { await api.delete(`/estimaciones/${current.id}/anexos/${aid}`); loadAnexos(current.id); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const viewAnexo = async (a) => {
    try {
      const res = await api.get(`/estimaciones/${current.id}/anexos/${a.id}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { toast.error("No se pudo abrir el anexo"); }
  };

  const openResumen = async (hasta) => {
    if (!pid) return;
    setCurrent(null);
    setResumenLoading(true);
    try {
      const params = hasta ? { hasta } : {};
      const { data } = await api.get(`/presupuestos/${pid}/resumen`, { params });
      setResumen(data);
      setResumenHasta(hasta ? String(hasta) : (data.hasta ? String(data.hasta) : ""));
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "No se pudo generar el resumen"); }
    finally { setResumenLoading(false); }
  };

  const downloadResumen = async (kind) => {
    try {
      const params = {};
      if (resumenHasta) params.hasta = Number(resumenHasta);
      const res = await api.get(`/presupuestos/${pid}/resumen/export/${kind}`, { params, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `resumen_presupuesto.${kind === "excel" ? "xlsx" : "pdf"}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "No se pudo exportar"); }
  };

  const t = current?.totales;

  return (
    <div className="flex flex-1 min-h-0" data-testid="estimaciones-view">
      {/* Sidebar lista */}
      <div className="w-72 shrink-0 border-r border-border flex flex-col">
        <div className="h-12 border-b border-border flex items-center px-3 gap-2">
          <ClipboardList className="h-4 w-4 text-primary" />
          <span className="font-head font-bold text-sm">Estimaciones</span>
        </div>
        <div className="p-3 border-b border-border space-y-2">
          <Field label="Presupuesto / Contrato">
            <select
              data-testid="estimacion-presupuesto-select"
              className="h-9 rounded-sm border border-input bg-background px-2 text-sm"
              value={pid}
              onChange={(e) => setPid(e.target.value)}
            >
              {presupuestos.length === 0 && <option value="">Sin presupuestos</option>}
              {presupuestos.map((p) => <option key={p.id} value={p.id}>{p.nombre}</option>)}
            </select>
          </Field>
          <Button className="w-full rounded-sm gap-2" disabled={!canWrite || !pid} onClick={crear} data-testid="nueva-estimacion-btn">
            <Plus className="h-4 w-4" /> Nueva estimación
          </Button>
          <input ref={importInputRef} type="file" accept=".json,application/json" className="hidden" onChange={importJson} data-testid="import-estimacion-input" />
          <Button variant="outline" className="w-full rounded-sm gap-2" disabled={!canWrite} onClick={() => importInputRef.current?.click()} data-testid="import-estimacion-btn">
            <Upload className="h-4 w-4" /> Importar (.json)
          </Button>
          <Button variant="outline" className="w-full rounded-sm gap-2" disabled={!pid} onClick={() => openResumen("")} data-testid="resumen-acumulado-btn">
            <BarChart3 className="h-4 w-4" /> Resumen acumulado
          </Button>
          {!canWrite && <p className="text-[11px] text-muted-foreground">Requiere suscripción activa y permiso de escritura.</p>}
        </div>
        <div className="overflow-y-auto flex-1">
          {list.map((e) => (
            <button
              key={e.id}
              onClick={() => open(e.id)}
              data-testid={`estimacion-item-${e.numero}`}
              className={`w-full text-left px-3 py-2 border-b border-border/60 transition-colors ${current?.id === e.id ? "bg-accent/60 border-l-2 border-l-primary" : "hover:bg-accent/40 border-l-2 border-l-transparent"}`}
            >
              <div className="text-sm font-medium flex items-center gap-1"><FileText className="h-3.5 w-3.5 shrink-0" /> Estimación #{e.numero}</div>
              <div className="text-xs text-muted-foreground truncate">
                {(e.periodo_inicio || "—")} → {(e.periodo_fin || "—")} · $ {fmt(e.totales?.periodo || 0, 2)}
              </div>
            </button>
          ))}
          {list.length === 0 && <div className="p-4 text-xs text-muted-foreground">Aún no hay estimaciones para este presupuesto.</div>}
        </div>
      </div>

      {/* Detalle */}
      <div className="flex-1 min-w-0 overflow-auto">
        {!current ? (
          resumen ? (
            <div className="p-5 space-y-5" data-testid="resumen-panel">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <button className="text-xs text-muted-foreground flex items-center gap-1 mb-1 hover:text-foreground" onClick={() => setResumen(null)}>
                    <ArrowLeft className="h-3 w-3" /> Volver
                  </button>
                  <h1 className="font-head font-black text-lg tracking-tight flex items-center gap-2"><BarChart3 className="h-5 w-5 text-primary" /> Resumen del presupuesto</h1>
                  <p className="text-sm text-muted-foreground">{resumen.presupuesto?.nombre} · {resumen.presupuesto?.cliente || "Sin cliente"} · {resumen.moneda_base}</p>
                </div>
                <div className="flex gap-1 flex-wrap items-center">
                  <Field label="Corte hasta estimación">
                    <select
                      data-testid="resumen-hasta-select"
                      className="h-9 rounded-sm border border-input bg-background px-2 text-sm"
                      value={resumenHasta}
                      onChange={(e) => openResumen(e.target.value)}
                    >
                      {list.map((e) => <option key={e.id} value={e.numero}>Estimación #{e.numero}</option>)}
                    </select>
                  </Field>
                  <Button size="sm" variant="outline" className="rounded-sm gap-1 mt-4" onClick={() => downloadResumen("excel")} data-testid="resumen-excel-btn"><FileSpreadsheet className="h-4 w-4" /> Excel</Button>
                  <Button size="sm" variant="outline" className="rounded-sm gap-1 mt-4" onClick={() => downloadResumen("pdf")} data-testid="resumen-pdf-btn"><FileText className="h-4 w-4" /> PDF</Button>
                </div>
              </div>

              {resumenLoading ? (
                <div className="p-10 text-sm text-muted-foreground flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Calculando…</div>
              ) : (
                <>
                  {/* Tarjetas globales */}
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="resumen-global">
                    {[
                      ["Contratado", resumen.global?.contratado],
                      ["Estimado acumulado", resumen.global?.estimado_acumulado],
                      ["Saldo por ejercer", resumen.global?.saldo_por_ejercer],
                    ].map(([l, v]) => (
                      <div key={l} className="border border-border rounded-sm p-3">
                        <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{l}</div>
                        <div className="font-mono-num text-base font-bold mt-1">$ {fmt(v || 0, 2)}</div>
                      </div>
                    ))}
                    <div className="border border-border rounded-sm p-3 bg-amber-500/10">
                      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Avance financiero</div>
                      <div className="font-mono-num text-base font-bold mt-1" data-testid="resumen-avance">{fmt(resumen.global?.avance_pct || 0, 1)}%</div>
                      <div className="h-2 rounded-full bg-muted overflow-hidden mt-1.5">
                        <div className="h-full bg-primary transition-all" style={{ width: `${Math.min(100, resumen.global?.avance_pct || 0)}%` }} />
                      </div>
                    </div>
                  </div>

                  {/* Tabla estimaciones */}
                  <div className="border border-border rounded-sm overflow-hidden">
                    <div className="px-3 h-9 flex items-center border-b border-border bg-muted/30 text-xs uppercase tracking-wide text-muted-foreground">Estimaciones del presupuesto</div>
                    <table className="qc-table">
                      <thead>
                        <tr>
                          <th style={{ width: 60 }}>Est. #</th>
                          <th>Periodo</th>
                          <th className="text-right">Imp. periodo</th>
                          <th className="text-right">Acumulado</th>
                          <th className="text-right">Saldo</th>
                          <th className="text-right" style={{ width: 70 }}>% av.</th>
                          <th className="text-right">Líquido</th>
                        </tr>
                      </thead>
                      <tbody>
                        {resumen.estimaciones?.map((e) => (
                          <tr key={e.numero} data-testid={`resumen-est-${e.numero}`}>
                            <td className="font-mono-num text-primary text-xs">#{e.numero}</td>
                            <td className="text-xs text-muted-foreground">{(e.periodo_inicio || "—")} → {(e.periodo_fin || "—")}</td>
                            <td className="text-right font-mono-num text-xs">$ {fmt(e.importe_periodo, 2)}</td>
                            <td className="text-right font-mono-num text-xs">$ {fmt(e.importe_acumulado, 2)}</td>
                            <td className="text-right font-mono-num text-xs">$ {fmt(e.saldo, 2)}</td>
                            <td className="text-right font-mono-num text-xs">{fmt(e.avance_pct, 1)}%</td>
                            <td className="text-right font-mono-num text-xs">$ {fmt(e.total_pagar, 2)}</td>
                          </tr>
                        ))}
                        {(!resumen.estimaciones || resumen.estimaciones.length === 0) && (
                          <tr><td colSpan={7} className="text-center text-muted-foreground py-6 text-xs">Aún no hay estimaciones.</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {/* Deducciones acumuladas */}
                    <div className="border border-border rounded-sm p-4 space-y-2" data-testid="resumen-deducciones">
                      <div className="text-xs uppercase tracking-wide text-muted-foreground flex items-center gap-1 mb-1"><TrendingUp className="h-3.5 w-3.5" /> Deducciones acumuladas</div>
                      {[
                        ["Importe de obra acumulado", resumen.global?.periodo_acumulado],
                        ["(-) Amortización de anticipo", -(resumen.global?.deducciones_acumuladas?.amortizacion || 0)],
                        ["(-) Retención / fondo de garantía", -(resumen.global?.deducciones_acumuladas?.retencion || 0)],
                        ["(+) IVA acumulado", resumen.global?.deducciones_acumuladas?.iva],
                      ].map(([l, v]) => (
                        <div key={l} className="flex items-center justify-between text-sm">
                          <span className="text-muted-foreground">{l}</span>
                          <span className="font-mono-num">$ {fmt(v || 0, 2)}</span>
                        </div>
                      ))}
                      <div className="flex items-center justify-between text-sm font-bold border-t border-border pt-2 mt-1 bg-amber-500/10 -mx-4 px-4 py-2">
                        <span>TOTAL LÍQUIDO PAGADO ACUM.</span>
                        <span className="font-mono-num" data-testid="resumen-total-liquido">$ {fmt(resumen.global?.deducciones_acumuladas?.total_pagar || 0, 2)}</span>
                      </div>
                    </div>

                    {/* Por partida */}
                    <div className="border border-border rounded-sm overflow-hidden">
                      <div className="px-3 h-9 flex items-center border-b border-border bg-muted/30 text-xs uppercase tracking-wide text-muted-foreground">Acumulado por partida</div>
                      <div className="max-h-80 overflow-auto">
                        <table className="qc-table">
                          <thead>
                            <tr>
                              <th>Partida</th>
                              <th className="text-right">Contratado</th>
                              <th className="text-right">Acumulado</th>
                              <th className="text-right" style={{ width: 70 }}>% av.</th>
                            </tr>
                          </thead>
                          <tbody>
                            {resumen.por_partida?.map((p, i) => (
                              <tr key={i} data-testid={`resumen-partida-${i}`}>
                                <td className="text-xs uppercase">{(p.codigo ? p.codigo + " " : "") + p.descripcion}</td>
                                <td className="text-right font-mono-num text-xs">$ {fmt(p.contratado, 2)}</td>
                                <td className="text-right font-mono-num text-xs">$ {fmt(p.acumulado, 2)}</td>
                                <td className="text-right font-mono-num text-xs">{fmt(p.avance_pct, 1)}%</td>
                              </tr>
                            ))}
                            {(!resumen.por_partida || resumen.por_partida.length === 0) && (
                              <tr><td colSpan={4} className="text-center text-muted-foreground py-6 text-xs">Sin partidas.</td></tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          ) : (
            <div className="p-10 text-muted-foreground text-sm">
              {loading || resumenLoading ? "Cargando…" : "Selecciona o crea una estimación. Captura las cantidades ejecutadas del periodo y el sistema calcula avance, deducciones e importe líquido a pagar."}
            </div>
          )
        ) : (
          <div className="p-5 space-y-5" data-testid="estimacion-detail">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <button className="text-xs text-muted-foreground flex items-center gap-1 mb-1 hover:text-foreground" onClick={() => setCurrent(null)}>
                  <ArrowLeft className="h-3 w-3" /> Volver
                </button>
                <h1 className="font-head font-black text-lg tracking-tight">Estimación #{current.numero}</h1>
                <p className="text-sm text-muted-foreground">{presuInfo?.nombre} · {presuInfo?.cliente || "Sin cliente"}</p>
              </div>
              <div className="flex gap-1 flex-wrap">
                {canWrite && <Button size="sm" className="rounded-sm gap-1" onClick={save} data-testid="guardar-estimacion-btn">Guardar</Button>}
                {canWrite && <Button size="sm" variant="outline" className="rounded-sm gap-1" onClick={analizarIA} disabled={aiLoading} data-testid="estimacion-ia-btn">{aiLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Analizar (IA)</Button>}
                <label className="flex items-center gap-1 text-xs cursor-pointer mr-1 select-none" data-testid="por-partida-toggle">
                  <input type="checkbox" checked={porPartida} onChange={(e) => setPorPartida(e.target.checked)} className="accent-primary h-3.5 w-3.5" />
                  Por partida
                </label>
                <Button size="sm" variant="outline" className="rounded-sm gap-1" onClick={() => download("excel")} data-testid="estimacion-excel-btn"><FileSpreadsheet className="h-4 w-4" /> Excel</Button>
                <Button size="sm" variant="outline" className="rounded-sm gap-1" onClick={() => download("pdf")} data-testid="estimacion-pdf-btn"><FileText className="h-4 w-4" /> PDF</Button>
                <Button size="sm" variant="outline" className="rounded-sm gap-1" onClick={exportJson} data-testid="estimacion-json-btn"><Download className="h-4 w-4" /> .json</Button>
                <Button size="sm" variant="outline" className="rounded-sm text-destructive" onClick={() => remove(current.id)} data-testid="eliminar-estimacion-btn"><Trash2 className="h-4 w-4" /></Button>
              </div>
            </div>

            {!keySet && <p className="text-[11px] text-amber-600">Para el análisis con IA, configura tu API key de Gemini en Administración → IA.</p>}

            {/* Anexos */}
            <div className="border border-border rounded-sm" data-testid="estimacion-anexos">
              <div className="flex items-center justify-between px-3 h-10 border-b border-border">
                <span className="text-xs font-semibold flex items-center gap-1"><Paperclip className="h-3.5 w-3.5" /> Anexos ({anexos.length})</span>
                {canWrite && (
                  <>
                    <input ref={anexoInputRef} type="file" multiple className="hidden" onChange={onAnexoFiles} data-testid="anexo-file-input" />
                    <Button size="sm" variant="outline" className="rounded-sm gap-1 h-7" onClick={() => anexoInputRef.current?.click()} data-testid="add-anexo-btn"><Plus className="h-3.5 w-3.5" /> Agregar</Button>
                  </>
                )}
              </div>
              {anexos.length === 0 ? (
                <p className="p-3 text-xs text-muted-foreground">Sin anexos. Agrega planos, fotos de avance, oficios o cualquier documento de respaldo.</p>
              ) : (
                <ul className="divide-y divide-border">
                  {anexos.map((a) => (
                    <li key={a.id} className="flex items-center justify-between px-3 py-2 gap-2" data-testid={`anexo-${a.id}`}>
                      <button className="flex items-center gap-2 min-w-0 text-left hover:text-primary" onClick={() => viewAnexo(a)}>
                        <FileText className="h-4 w-4 shrink-0" />
                        <span className="truncate text-sm">{a.nombre}</span>
                        <span className="text-[10px] text-muted-foreground font-mono-num shrink-0">{(a.size / 1024).toFixed(0)} KB</span>
                      </button>
                      {canWrite && <button className="text-destructive shrink-0" onClick={() => removeAnexo(a.id)} data-testid={`del-anexo-${a.id}`} title="Eliminar"><Trash2 className="h-3.5 w-3.5" /></button>}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Encabezado editable */}
            <div className="grid grid-cols-2 md:grid-cols-6 gap-3 border border-border rounded-sm p-3">
              <Field label="Periodo inicio"><Input type="date" disabled={!canWrite} value={current.periodo_inicio || ""} onChange={(e) => setHeader("periodo_inicio", e.target.value)} data-testid="periodo-inicio-input" className="h-9 rounded-sm" /></Field>
              <Field label="Periodo fin"><Input type="date" disabled={!canWrite} value={current.periodo_fin || ""} onChange={(e) => setHeader("periodo_fin", e.target.value)} data-testid="periodo-fin-input" className="h-9 rounded-sm" /></Field>
              <Field label="Anticipo %"><Input type="number" disabled={!canWrite} value={current.anticipo_pct ?? 0} onChange={(e) => setHeader("anticipo_pct", e.target.value)} className="h-9 rounded-sm font-mono-num" /></Field>
              <Field label="Amort. anticipo %"><Input type="number" disabled={!canWrite} value={current.amortizacion_pct ?? 0} onChange={(e) => setHeader("amortizacion_pct", e.target.value)} data-testid="amortizacion-input" className="h-9 rounded-sm font-mono-num" /></Field>
              <Field label="Retención %"><Input type="number" disabled={!canWrite} value={current.retencion_pct ?? 0} onChange={(e) => setHeader("retencion_pct", e.target.value)} data-testid="retencion-input" className="h-9 rounded-sm font-mono-num" /></Field>
              <Field label="IVA %"><Input type="number" disabled={!canWrite} value={current.iva_pct ?? 0} onChange={(e) => setHeader("iva_pct", e.target.value)} className="h-9 rounded-sm font-mono-num" /></Field>
            </div>

            {/* Tabla de conceptos */}
            <div className="border border-border rounded-sm overflow-hidden">
              <div className="px-3 h-9 flex items-center border-b border-border bg-muted/30 text-xs uppercase tracking-wide text-muted-foreground">
                Conceptos — captura de avance del periodo
              </div>
              <table className="qc-table">
                <thead>
                  <tr>
                    <th style={{ width: 90 }}>Clave</th>
                    <th>Concepto</th>
                    <th style={{ width: 50 }}>Un.</th>
                    <th style={{ width: 90 }} className="text-right">P.U. venta</th>
                    <th style={{ width: 90 }} className="text-right">Contrat.</th>
                    <th style={{ width: 80 }} className="text-right">Anterior</th>
                    <th style={{ width: 110 }} className="text-right">Este periodo</th>
                    <th style={{ width: 80 }} className="text-right">Acum.</th>
                    <th style={{ width: 70 }} className="text-right">% av.</th>
                    <th style={{ width: 110 }} className="text-right">Importe per.</th>
                  </tr>
                </thead>
                <tbody>
                  {current.conceptos?.map((r) => (
                    <tr key={r.node_id} data-testid={`est-row-${r.codigo}`}>
                      <td className="font-mono-num text-primary text-xs">{r.codigo}</td>
                      <td className="text-xs">{r.descripcion}</td>
                      <td className="font-mono-num text-xs text-muted-foreground">{r.unidad}</td>
                      <td className="text-right font-mono-num text-xs">$ {fmt(r.pu_venta, 2)}</td>
                      <td className="text-right font-mono-num text-xs">{fmt(r.cant_contratada, 2)}</td>
                      <td className="text-right font-mono-num text-xs text-muted-foreground">{fmt(r.cant_anterior, 2)}</td>
                      <td className="text-right">
                        <Input
                          type="number"
                          disabled={!canWrite}
                          value={r.cant_periodo}
                          onChange={(e) => setCant(r.node_id, e.target.value)}
                          data-testid={`est-cant-${r.codigo}`}
                          className="h-8 w-24 ml-auto rounded-sm text-right font-mono-num"
                        />
                      </td>
                      <td className="text-right font-mono-num text-xs">{fmt(r.cant_acumulada, 2)}</td>
                      <td className="text-right font-mono-num text-xs">
                        <span className={r.avance_pct > 100 ? "text-destructive" : r.avance_pct >= 100 ? "text-emerald-600" : ""}>{fmt(r.avance_pct, 1)}%</span>
                      </td>
                      <td className="text-right font-mono-num text-xs">$ {fmt(r.importe_periodo, 2)}</td>
                    </tr>
                  ))}
                  {(!current.conceptos || current.conceptos.length === 0) && (
                    <tr><td colSpan={10} className="text-center text-muted-foreground py-6 text-xs">El presupuesto no tiene conceptos.</td></tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Resumen financiero */}
            {t && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className="border border-border rounded-sm p-4 space-y-2" data-testid="estimacion-resumen">
                  <div className="text-xs uppercase tracking-wide text-muted-foreground flex items-center gap-1 mb-1"><TrendingUp className="h-3.5 w-3.5" /> Resumen financiero</div>
                  {[
                    ["Importe de obra del periodo", t.periodo],
                    ["(-) Amortización de anticipo", -t.amortizacion],
                    ["(-) Retención / fondo de garantía", -t.retencion],
                    [`(+) IVA (${current.iva_pct || 0}%)`, t.iva],
                  ].map(([l, v]) => (
                    <div key={l} className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">{l}</span>
                      <span className="font-mono-num">$ {fmt(v, 2)}</span>
                    </div>
                  ))}
                  <div className="flex items-center justify-between text-sm font-bold border-t border-border pt-2 mt-1 bg-amber-500/10 -mx-4 px-4 py-2">
                    <span>TOTAL A PAGAR (líquido)</span>
                    <span className="font-mono-num" data-testid="estimacion-total-pagar">$ {fmt(t.total_pagar, 2)}</span>
                  </div>
                </div>
                <div className="border border-border rounded-sm p-4 space-y-2">
                  <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Avance de la obra</div>
                  <div className="flex items-center justify-between text-sm"><span className="text-muted-foreground">Importe contratado</span><span className="font-mono-num">$ {fmt(t.contratado, 2)}</span></div>
                  <div className="flex items-center justify-between text-sm"><span className="text-muted-foreground">Acumulado ejecutado</span><span className="font-mono-num">$ {fmt(t.acumulado, 2)}</span></div>
                  <div className="flex items-center justify-between text-sm"><span className="text-muted-foreground">Saldo por ejecutar</span><span className="font-mono-num">$ {fmt(t.saldo, 2)}</span></div>
                  <div className="pt-1">
                    <div className="flex items-center justify-between text-xs mb-1"><span>Avance acumulado</span><span className="font-mono-num font-bold">{fmt(t.avance_pct, 1)}%</span></div>
                    <div className="h-2.5 rounded-full bg-muted overflow-hidden">
                      <div className="h-full bg-primary transition-all" style={{ width: `${Math.min(100, t.avance_pct)}%` }} />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Análisis IA */}
            {current.ia && (current.ia.resumen || current.ia.observaciones?.length > 0) && (
              <div className="border border-amber-500/40 rounded-sm" data-testid="estimacion-ia-result">
                <div className="px-3 h-9 flex items-center gap-2 border-b border-amber-500/30 bg-amber-500/10 text-xs uppercase tracking-wide text-amber-700 dark:text-amber-400">
                  <Sparkles className="h-3.5 w-3.5" /> Análisis de la residencia (IA)
                </div>
                <div className="p-4 space-y-3 text-sm">
                  {current.ia.resumen && <p>{current.ia.resumen}</p>}
                  {current.ia.observaciones?.length > 0 && (
                    <div>
                      <div className="font-medium text-xs uppercase text-muted-foreground mb-1">Observaciones</div>
                      <ul className="list-disc pl-5 space-y-1 text-sm">{current.ia.observaciones.map((o, i) => <li key={i}>{o}</li>)}</ul>
                    </div>
                  )}
                  {current.ia.desviaciones?.length > 0 && (
                    <div className="space-y-2">
                      <div className="font-medium text-xs uppercase text-muted-foreground flex items-center gap-1"><AlertTriangle className="h-3.5 w-3.5 text-amber-500" /> Desviaciones</div>
                      {current.ia.desviaciones.map((d, i) => (
                        <div key={i} className="border border-border/60 rounded-sm p-2">
                          <div className="flex items-center justify-between gap-2">
                            <span className="font-medium text-sm">{d.concepto}</span>
                            <span className={`text-[10px] uppercase px-2 py-0.5 rounded-sm border ${SEV[(d.severidad || "baja").toLowerCase()] || SEV.baja}`}>{d.severidad}</span>
                          </div>
                          <p className="text-xs text-muted-foreground mt-1">{d.nota}</p>
                        </div>
                      ))}
                    </div>
                  )}
                  {current.ia.recomendaciones?.length > 0 && (
                    <div>
                      <div className="font-medium text-xs uppercase text-muted-foreground flex items-center gap-1"><Lightbulb className="h-3.5 w-3.5 text-amber-500" /> Recomendaciones</div>
                      <ul className="list-disc pl-5 space-y-1 text-sm">{current.ia.recomendaciones.map((o, i) => <li key={i}>{o}</li>)}</ul>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
